import React from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import ClearIcon from "@mui/icons-material/Clear";

interface EditModeToolbarProps {
    /** Is currently in edit mode? */
    isEditMode: boolean;
    /** Is save operation in progress? */
    isSaving: boolean;
    /** Error from previous save attempt, if any */
    error: Error | null;
    /** Callback to enter edit mode */
    onEnterEdit: () => void;
    /** Callback to save changes */
    onSave: () => Promise<void>;
    /** Callback to discard changes */
    onDiscard: () => void;
    /** Callback to clear error */
    onClearError: () => void;
}

/**
 * Toolbar component for edit mode UI
 *
 * Shows different UI depending on mode:
 * - View mode: Single "Edit" button
 * - Edit mode: "Save" and "Discard" buttons + error alerts
 *
 * Follows accessibility best practices with proper ARIA labels and keyboard support
 */
export const EditModeToolbar: React.FC<EditModeToolbarProps> = ({
    isEditMode,
    isSaving,
    error,
    onEnterEdit,
    onSave,
    onDiscard,
    onClearError,
}) => {
    const { t } = useTranslation();

    // View mode: Show simple Edit button
    if (!isEditMode) {
        return (
            <Box sx={{ mb: 2 }}>
                <Button
                    startIcon={<EditIcon />}
                    onClick={onEnterEdit}
                    variant="outlined"
                    color="primary"
                    aria-label={t("aria.enter_edit_mode")}
                >
                    {t("common.edit")}
                </Button>
            </Box>
        );
    }

    // Edit mode: Show Save/Discard + error handling
    return (
        <Box sx={{ mb: 2 }}>
            {error && (
                <Alert
                    severity="error"
                    onClose={onClearError}
                    role="alert"
                    sx={{ mb: 2 }}
                >
                    <strong>{t("errors.reorder_failed")}:</strong>{" "}
                    {error.message}
                </Alert>
            )}

            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Button
                    startIcon={
                        isSaving ? <CircularProgress size={20} /> : <SaveIcon />
                    }
                    onClick={onSave}
                    variant="contained"
                    color="primary"
                    disabled={isSaving}
                    aria-label={t("aria.save_order")}
                    aria-busy={isSaving}
                >
                    {t("common.save")}
                </Button>

                <Button
                    startIcon={<ClearIcon />}
                    onClick={onDiscard}
                    variant="outlined"
                    disabled={isSaving}
                    aria-label={t("aria.discard_changes")}
                >
                    {t("common.discard")}
                </Button>

                {isSaving && (
                    <Box
                        role="status"
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            ml: 2,
                            color: "primary.main",
                        }}
                    >
                        <CircularProgress size={20} />
                        <Typography
                            component="span"
                            sx={{
                                ml: 1,
                                fontSize: "0.875rem",
                            }}
                        >
                            {t("common.saving")}...
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default EditModeToolbar;
